import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Code2, Sun, Moon, User, ArrowLeft, Save, LogOut, Check, Heart, Shield, Laugh
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { usersApi, ttsApi } from '../lib/api';
import { toast } from 'sonner';
import { AVATAR_OPTIONS } from '../components/avatarDefinitions';
import RecentSignInsCard from '../components/profile/RecentSignInsCard';

// Mini avatar preview for selection grid
function AvatarPreview({ avatar, size = 60 }) {
  return (
    <img src={avatar.image} alt={avatar.name}
      className="mx-auto rounded-lg object-contain object-bottom"
      style={{ width: size, height: size }}
      draggable={false}
    />
  );
}

export default function ProfilePage() {
  const { user, logout, updateUser } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    grade: user?.grade?.toString() || '',
    avatar_id: user?.avatar_id || 'male-1',
    avatar_personality: user?.avatar_personality || 'encouraging'
  });
  const [prelearnProgress, setPrelearnProgress] = useState(null);

  // All lab IDs and descriptions for pre-learning
  const ALL_LABS = [
    { id: 'english', title: 'English Language', text: 'English Language Lab covering grammar, vocabulary, synonyms, antonyms, reading comprehension, and creative writing.' },
    { id: 'computer-science', title: 'Computer Science', text: 'Computer Science Lab covering programming basics, algorithms, data structures, binary, and debugging.' },
    { id: 'road-safety', title: 'Road Safety', text: 'Road Safety Lab covering traffic rules, signs, pedestrian safety, and first aid basics.' },
    { id: 'civic-manners', title: 'Civic Manners', text: 'Civic Manners Lab covering citizenship, democracy, rights, duties, and respectful behavior.' },
    { id: 'physics', title: 'Physics', text: 'Physics Projectile Motion Lab covering gravity, trajectory, angle of launch, and real-time simulation.' },
    { id: 'chemistry', title: 'Chemistry', text: 'Chemistry Molecule Builder Lab covering atoms, bonds, molecules, and chemical reactions.' },
    { id: 'sorting', title: 'Sorting Algorithms', text: 'Sorting Algorithm Arena comparing Bubble Sort, Selection Sort, and Merge Sort with visual animations.' },
    { id: 'history', title: 'History Timeline', text: 'History Timeline Lab covering Ancient, Medieval, and Modern Indian history with drag-and-drop events.' },
    { id: 'geography', title: 'Geography', text: 'Geography Explorer covering Indian states, capitals, rivers, mountain ranges, and world landmarks.' },
    { id: 'geometry', title: 'Geometry', text: 'Geometry Playground covering shapes, angles, areas, perimeters, and coordinate geometry.' },
    { id: 'algebra', title: 'Algebra', text: 'Algebra Balance Lab covering equations, variables, operations, and solving step-by-step.' },
    { id: 'electrical', title: 'Electrical Circuits', text: 'Electrical Circuits Lab covering batteries, bulbs, resistors, Ohms Law, and circuit building.' },
    { id: 'drone', title: 'Drone Programming', text: 'Drone Flight Lab covering visual block programming, flight commands, navigation, and obstacle avoidance.' },
    { id: 'ai-prompt', title: 'AI Prompt Engineering', text: 'AI Prompt Lab covering prompt design, context, constraints, and testing with real AI models.' },
    { id: 'mining', title: 'Mining Science', text: 'Mining Science Lab covering mineral extraction, geological layers, and safety procedures.' },
    { id: 'microbiology', title: 'Microbiology', text: 'Microbiology Lab covering bacteria, viruses, cells, microscopy, and hygiene.' },
    { id: 'visual-labs-hub', title: 'Visual Labs Hub', text: 'Welcome to 53 interactive STEM labs covering physics, chemistry, biology, computer science, and more.' },
  ];

  // Poll pre-learn progress
  useEffect(() => {
    if (!prelearnProgress || prelearnProgress.status === 'done') return;
    const interval = setInterval(async () => {
      try {
        const status = await ttsApi.prelearnStatus();
        setPrelearnProgress(status);
        if (status.status === 'done') {
          clearInterval(interval);
          toast.success('Avatar has learned all labs!');
        }
      } catch { /* ignore */ }
    }, 2000);
    return () => clearInterval(interval);
  }, [prelearnProgress]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updates = { name: formData.name, avatar_id: formData.avatar_id, avatar_personality: formData.avatar_personality };
      const newGrade = formData.grade ? parseInt(formData.grade) : null;
      if (newGrade && newGrade !== user?.grade) {
        updates.grade = newGrade;
      }

      await usersApi.update(user.user_id, updates);
      updateUser({ ...user, ...updates });
      toast.success('Profile updated! Avatar is now learning your courses...');

      // Start pre-learning all lab narrations in background
      const lang = localStorage.getItem('preferredLanguage') || 'en';
      const langMap = { en: 'English', hi: 'Hindi', ta: 'Tamil', te: 'Telugu', kn: 'Kannada', ml: 'Malayalam', bn: 'Bengali', mr: 'Marathi', gu: 'Gujarati', pa: 'Punjabi', or: 'Odia', as: 'Assamese' };
      try {
        await ttsApi.prelearn(ALL_LABS, lang, langMap[lang] || 'English');
        setPrelearnProgress({ total: ALL_LABS.length, completed: 0, status: 'learning', current: '' });
      } catch { /* non-critical */ }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const roleLabels = {
    super_admin: 'Super Admin',
    org_admin: 'School Admin',
    faculty: 'Faculty',
    org_student: 'School Student',
    independent_student: 'Independent Learner'
  };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      {/* Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="gradient-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
      </div>

      {/* Header */}
      <header className="relative z-10 glass border-b border-border/50 sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold">CognitiaIndia</span>
            </Link>

            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="rounded-full"
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                      ) : (
                        <User className="w-4 h-4 text-primary" />
                      )}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-2xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          className="mb-6"
          onClick={() => navigate('/dashboard')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>

        <div className="space-y-8">
          {/* Profile Header */}
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 rounded-2xl bg-primary/10 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-24 h-24 rounded-2xl object-cover" />
              ) : (
                <User className="w-12 h-12 text-primary" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{user?.name}</h1>
              <p className="text-muted-foreground">{user?.email}</p>
              <span className="inline-block mt-2 px-3 py-1 rounded-full text-sm bg-primary/10 text-primary font-medium">
                {roleLabels[user?.role]}
              </span>
            </div>
          </div>

          {/* Profile Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-6 rounded-xl bg-card border border-border space-y-4">
              <h2 className="text-lg font-semibold">Profile Information</h2>

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Your name"
                  className="h-11"
                  data-testid="profile-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user?.email}
                  disabled
                  className="h-11 bg-muted"
                />
                <p className="text-xs text-muted-foreground">Email cannot be changed</p>
              </div>

              {['org_student', 'independent_student'].includes(user?.role) && (
                <div className="space-y-2">
                  <Label htmlFor="grade">Grade Level</Label>
                  <Select value={formData.grade} onValueChange={(v) => setFormData({ ...formData, grade: v })}>
                    <SelectTrigger className="h-11" data-testid="profile-grade-select">
                      <SelectValue placeholder="Select your grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 6, 7, 8, 9, 10].map(g => (
                        <SelectItem key={g} value={g.toString()}>Grade {g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Teacher Avatar Selection */}
              <div className="space-y-3 pt-2">
                <Label>Teacher Avatar</Label>
                <p className="text-xs text-muted-foreground">Choose your teacher avatar that guides you through labs</p>
                <div className="grid grid-cols-3 gap-3" data-testid="avatar-selection-grid">
                  {AVATAR_OPTIONS.map(av => (
                    <button key={av.id} type="button"
                      onClick={() => setFormData({ ...formData, avatar_id: av.id })}
                      data-testid={`avatar-option-${av.id}`}
                      className={`relative p-3 rounded-xl border-2 transition-all text-center hover:scale-105
                        ${formData.avatar_id === av.id ? 'border-primary bg-primary/10 ring-2 ring-primary/30' : 'border-border bg-card hover:border-primary/40'}`}
                    >
                      {formData.avatar_id === av.id && (
                        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <AvatarPreview avatar={av} size={60} />
                      <p className="text-xs font-medium mt-1">{av.name}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{av.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Teaching Personality */}
              <div className="space-y-3 pt-2">
                <Label>Teaching Personality</Label>
                <p className="text-xs text-muted-foreground">How should your avatar teach? This changes the narration style.</p>
                <div className="grid grid-cols-3 gap-3" data-testid="personality-selection-grid">
                  {[
                    { id: 'encouraging', name: 'Encouraging', desc: 'Warm & supportive', icon: Heart, color: 'text-green-400', border: 'border-green-500', bg: 'bg-green-500/10' },
                    { id: 'strict', name: 'Strict', desc: 'Focused & precise', icon: Shield, color: 'text-orange-400', border: 'border-orange-500', bg: 'bg-orange-500/10' },
                    { id: 'humorous', name: 'Humorous', desc: 'Fun & witty', icon: Laugh, color: 'text-yellow-400', border: 'border-yellow-500', bg: 'bg-yellow-500/10' },
                  ].map(p => (
                    <button key={p.id} type="button"
                      onClick={() => setFormData({ ...formData, avatar_personality: p.id })}
                      data-testid={`personality-${p.id}`}
                      className={`relative p-3 rounded-xl border-2 transition-all text-center hover:scale-105
                        ${formData.avatar_personality === p.id ? `${p.border} ${p.bg} ring-2 ring-${p.border}/30` : 'border-border bg-card hover:border-muted-foreground/40'}`}
                    >
                      {formData.avatar_personality === p.id && (
                        <div className={`absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-primary flex items-center justify-center`}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                      <p.icon className={`w-6 h-6 ${p.color} mx-auto`} />
                      <p className="text-xs font-semibold mt-1.5">{p.name}</p>
                      <p className="text-[10px] text-muted-foreground">{p.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="btn-primary w-full h-11"
              disabled={loading}
              data-testid="save-profile-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>

            {/* Pre-learning progress bar */}
            {prelearnProgress && prelearnProgress.status === 'learning' && (
              <div className="mt-4 p-4 rounded-xl bg-blue-950/30 border border-blue-800/40" data-testid="prelearn-progress">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-blue-300">Avatar is learning your courses...</span>
                  <span className="text-xs text-blue-400">{prelearnProgress.completed}/{prelearnProgress.total}</span>
                </div>
                <div className="w-full h-2 rounded-full bg-blue-900/50 overflow-hidden">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400"
                    animate={{ width: `${(prelearnProgress.completed / prelearnProgress.total) * 100}%` }}
                    transition={{ duration: 0.5 }}
                  />
                </div>
                {prelearnProgress.current && (
                  <p className="mt-1.5 text-xs text-blue-400/70">Learning: {prelearnProgress.current}</p>
                )}
              </div>
            )}
            {prelearnProgress && prelearnProgress.status === 'done' && (
              <div className="mt-4 p-3 rounded-xl bg-emerald-950/30 border border-emerald-800/40 text-sm text-emerald-300" data-testid="prelearn-done">
                Avatar has finished learning all courses! Labs will load instantly now.
              </div>
            )}
          </form>

          {/* Account Info */}
          <div className="p-6 rounded-xl bg-card border border-border">
            <h2 className="text-lg font-semibold mb-4">Account Information</h2>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">User ID</span>
                <span className="font-mono">{user?.user_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Role</span>
                <span>{roleLabels[user?.role]}</span>
              </div>
              {user?.school_id && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">School</span>
                  <span>{user.school_id}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Member Since</span>
                <span>{new Date(user?.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Recent Sign-Ins (Phase 39.4) */}
          <RecentSignInsCard />

          {/* Danger Zone */}
          <div className="p-6 rounded-xl border border-destructive/50 bg-destructive/5">
            <h2 className="text-lg font-semibold text-destructive mb-4">Danger Zone</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Once you log out, you will need to sign in again to access your account.
            </p>
            <Button
              variant="destructive"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
